'use client'

import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { LineGlobe } from '@qr-front/icons/line-globe'
import { theme } from '@qr-front/ui'
import { buttonCls } from '@qr-front/ui/components/Button'
import { Spinner } from '@qr-front/ui/components/Spinner'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import {
	documentCompanyNameAtom,
	documentFileDescriptionAtom,
	documentTitleAtom,
	documentWebsiteAtom,
} from 'features/generator/contentFormSections/PdfForm/DocumentInfo'
import {
	pdfAtom,
	pdfIsDirectAtom,
} from 'features/generator/contentFormSections/PdfForm/PdfFile'
import { QrScreen } from 'features/generator/QrScreen'
import { TQrScreenProps } from 'features/generator/QrScreen/QrScreen'
import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import React, { useMemo, useRef } from 'react'
import { buildResourceUrl } from 'services/mediaStorage'
import { addMissingUrlProtocol } from 'utils/urlUtils'

import {
	primaryColorAtom,
	secondaryColorAtom,
} from '@/content-from-sections/client'

import { useColoredButton } from '../../hooks/useColoredButton'
import { IsPreviewModeProvider } from './PdfScreenContexts'

export const PDF_DEMO_PRIMARY_COLOR = getVersionForClone({
	base: '#FF9179',
	clone1: '#FFA66A',
})

const PDF_IMAGE = '/web-app/qr-preview-demos/pdf/pdf-screen.webp'

type TPdfScreenProps = {
	previewMode: boolean
	demoMode: boolean
}

const DemoButton = () => {
	const t = useTranslations()

	const { coloredButtonCls, coloredButtonStyle } = useColoredButton('black')

	return (
		<div
			className={cn(
				buttonCls({ visual: 'secondary' }),
				coloredButtonCls,
				'w-full grow text-body-caption font-normal',
				'rounded-6',
			)}
			style={coloredButtonStyle}
		>
			{t('generator__qr_view__pdf__see_pdf')}
		</div>
	)
}

const CustomButton = () => {
	const t = useTranslations()

	const pdf = useAtomValue(pdfAtom)

	const fileUrl = useMemo(() => {
		return pdf?.publicId ? buildResourceUrl(pdf) : ''
	}, [pdf])

	const secondaryColor = useAtomValue(secondaryColorAtom)

	const { coloredButtonCls, coloredButtonStyle } =
		useColoredButton(secondaryColor)

	return (
		<a
			href={addMissingUrlProtocol(fileUrl)}
			rel="nofollow ugc noreferrer"
			target="_blank"
			className={cn(
				buttonCls({ visual: 'secondary' }),
				coloredButtonCls,
				'z-10 w-full grow text-body-caption font-normal',
				'rounded-6',
			)}
			style={coloredButtonStyle}
		>
			{t('generator__qr_view__pdf__see_pdf')}
		</a>
	)
}

const PdfContent = ({ previewMode }: { previewMode: boolean }) => {
	const pdf = useAtomValue(pdfAtom)
	const pdfIsDirect = useAtomValue(pdfIsDirectAtom)

	const missingPdfPlaceholder = <div className="h-[250px]" />

	const fileUrl = useMemo(() => {
		return pdf?.publicId ? buildResourceUrl(pdf) : ''
	}, [pdf])

	const [pdfComponents, setPdfComponents] = React.useState<{
		Document?: typeof import('react-pdf').Document
		Page?: typeof import('react-pdf').Page
		pdfjs?: typeof import('react-pdf').pdfjs
	} | null>(null)
	const [loading, setLoading] = React.useState(false)

	React.useEffect(() => {
		let isMounted = true
		if (pdf && !pdfComponents) {
			setLoading(true)
			import('react-pdf').then((mod) => {
				if (isMounted) {
					mod.pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${mod.pdfjs.version}/legacy/build/pdf.worker.min.mjs`
					setPdfComponents({
						Document: mod.Document,
						Page: mod.Page,
						pdfjs: mod.pdfjs,
					})
					setLoading(false)
				}
			})
		}
		return () => {
			isMounted = false
		}
	}, [pdf, pdfComponents])

	const getDevicePixelRatio = () => {
		if (typeof window === 'undefined') {
			return 2
		}
		return Math.min(2, window.devicePixelRatio || 2)
	}
	if (pdfIsDirect && !previewMode) {
		return missingPdfPlaceholder
	}

	if (!pdf) {
		return missingPdfPlaceholder
	}

	if (loading || !pdfComponents) {
		return missingPdfPlaceholder
	}
	const { Document, Page } = pdfComponents
	if (!Document || !Page) {
		return missingPdfPlaceholder
	}

	return (
		<div className={cn('w-full overflow-hidden', pdfIsDirect && 'h-full')}>
			{pdf ? (
				<Document
					file={fileUrl}
					className={cn(
						'h-full max-h-[250px] w-full tablet:max-h-full',
						previewMode && '!max-h-[250px]',
					)}
					loading={
						<div className="flex h-[250px] w-full items-center justify-center">
							<Spinner />
						</div>
					}
				>
					<Page
						pageNumber={1}
						_className="!h-full !w-full block"
						devicePixelRatio={getDevicePixelRatio()}
						{...(previewMode && { width: 204, height: 250 })}
					/>
				</Document>
			) : null}
		</div>
	)
}

export const Content = ({ previewMode, demoMode }: TPdfScreenProps) => {
	const t = useTranslations()
	const pdfTitleRef = useRef<HTMLDivElement>(null)

	const primaryColor = useAtomValue(primaryColorAtom)
	const secondaryColor = useAtomValue(secondaryColorAtom)
	const documentTitle = useAtomValue(documentTitleAtom)
	const documentFileDescription = useAtomValue(documentFileDescriptionAtom)
	const documentCompanyName = useAtomValue(documentCompanyNameAtom)
	const documentWebsite = useAtomValue(documentWebsiteAtom)
	const pdfIsDirect = useAtomValue(pdfIsDirectAtom)

	const backgroundColor = demoMode ? PDF_DEMO_PRIMARY_COLOR : primaryColor

	return (
		<IsPreviewModeProvider value={previewMode}>
			<div className={cn('relative', demoMode && 'pointer-events-none')}>
				{pdfIsDirect ? (
					<div className="h-full w-full">
						<PdfContent previewMode={previewMode} />
					</div>
				) : (
					<div className="flex w-full flex-col items-center">
						<div
							className={cn(
								'flex min-h-[244px] w-full flex-col items-center gap-5 px-5 py-8 pb-36 transition-colors',
								previewMode && 'pt-6',
							)}
							style={{
								backgroundColor: backgroundColor,
							}}
						>
							<div
								ref={pdfTitleRef}
								className={cn(
									'flex max-w-full',
									previewMode && 'max-w-[220px]',
								)}
								style={{ color: demoMode ? undefined : secondaryColor }}
							>
								<div className="flex w-full max-w-[680px] flex-col text-center text-white word-break-fix">
									<span
										className="type-body-caption break-words break-keep text-10 leading-[16px]"
										style={{ color: secondaryColor }}
									>
										{!demoMode
											? documentCompanyName
											: t('generator__qr_view__pdf__default_name')}
									</span>
									<h4
										className="type-heading-4 break-words break-keep"
										style={{ color: secondaryColor }}
									>
										{!demoMode
											? documentTitle
											: t('generator__qr_view__pdf__default_title')}
									</h4>
									<span
										className="type-body-caption break-words break-keep text-center text-10 leading-[16px]"
										style={{ color: secondaryColor }}
									>
										{!demoMode
											? documentFileDescription
											: t('generator__qr_view__pdf__default_description')}
									</span>
								</div>
							</div>
						</div>
						<div className="mt-[-142px] flex w-full max-w-[680px] flex-col gap-5 p-5">
							<div className="flex flex-col gap-2 rounded-6 bg-white p-2 shadow-generator">
								{!demoMode ? (
									<PdfContent previewMode={previewMode} />
								) : (
									// eslint-disable-next-line @next/next/no-img-element
									<img
										src={PDF_IMAGE}
										alt="PDF"
										className="h-full w-full rounded-6 object-cover"
									/>
								)}
								{demoMode ? <DemoButton /> : <CustomButton />}
							</div>
							{(demoMode || documentWebsite) && (
								<div className="mt-1 flex items-center justify-center gap-2 pb-3 text-black word-break-fix">
									<LineGlobe className="h-4 w-4" />
									{!demoMode ? (
										<a
											href={addMissingUrlProtocol(documentWebsite)}
											target="_blank"
											rel="noopener noreferrer"
											className="leading-4 type-body-caption text-10"
										>
											{documentWebsite}
										</a>
									) : (
										<span className="leading-4 type-body-caption text-10">
											{t('generator__qr_view__pdf__default_website')}
										</span>
									)}
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</IsPreviewModeProvider>
	)
}

export const PdfScreen = (props: TQrScreenProps) => {
	const primaryColor = useAtomValue(primaryColorAtom)
	const pdfIsDirect = useAtomValue(pdfIsDirectAtom)

	const PRIMARY_COLOR = props.demoMode
		? PDF_DEMO_PRIMARY_COLOR
		: pdfIsDirect
			? theme.colors.white
			: primaryColor

	return (
		<QrScreen
			{...props}
			primaryColor={PRIMARY_COLOR}
			content={<Content {...props} />}
		/>
	)
}
